import useCreateSpotifyReport from 'wb-spotify/app/routes/reports/create/useCreateSpotifyReport';
import { useMutation } from '@apollo/client';
import dayjs from 'dayjs';
import { DATE_PARSE_FORMAT } from '@aleph/shared-components';
import {
	SpotifyReportsDocument,
	CreateSpotifyReportDocument,
	UpdateSpotifyReportDocument,
	ReportDateRangeType as SpotifyCreateReportDateRangeType,
	ReportActionType as SpotifyCreateReportActionType,
	ReportActionFormat as SpotifyCreateReportActionFormat,
	ReportActionOptionsTypes as SpotifyCreateReportActionOptionType,
} from 'wb-spotify/graphql/generated';
import useSupergridSettings from 'wb-spotify/app/routes/superGrid/hooks/useSupergridSettings';
import { SpotifyReportSchedule } from 'wb-spotify/app/common/utils';

import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';

const getReportScheduleFromSchedule = (schedule: SpotifyReportSchedule, time: string) => {
	switch (schedule) {
		case SpotifyReportSchedule.OnlyOnce:
			return time;
		case SpotifyReportSchedule.EveryMonday:
			return 'every_monday';
		case SpotifyReportSchedule.EveryWeekday:
			return 'every_weekday';
		case SpotifyReportSchedule.FirstOfTheMonth:
			return 'first_of_the_month';
		default:
			return 'only_once';
	}
};

const useCreateReportMutation = () => {
	const {
		schedule, breakdowns, metrics, filters, name, dates, reportId, time, userGroupIds, privateUserGroupIds,
	} = useCreateSpotifyReport();
	const { lifetimeStats } = useSupergridSettings();

	const [onCreate, { loading, error: creationError }] = useMutation(CreateSpotifyReportDocument, {
		refetchQueries:      [SpotifyReportsDocument],
		awaitRefetchQueries: true,
	});
	const [onUpdate, { loading: loadingUpdate, error: loadingError }] = useMutation(UpdateSpotifyReportDocument);
	const userMetaData = useUserMetaData();

	const onCreateReport = async () => {
		if (!dates) {
			return;
		}

		const variables = {
			// @ts-ignore: reportId will not be present on onCreate
			reportId,
			// TODO check later maybe we can change it
			reportDataset:        'endpoint_spotify_statistics',
			reportDimensions:     breakdowns,
			// TODO later change in case if new filters
			reportFiltersOptions: [{
				name: 'userGroupIds', label: 'User Group ID', value: filters.userGroupId[0].value, filter: 'filter', action: 'INCLUDE',
			}],
			reportConditions:       null,
			reportMetrics:          metrics,
			reportHideZeroRows:     true,
			reportName:             name,
			reportDateRangeOptions: {
				dateRangeType:  SpotifyCreateReportDateRangeType.DateSchedule,
				dateRangeValue: [
					dayjs(dates.from)?.format(DATE_PARSE_FORMAT) ?? dayjs().subtract(1, 'week').format(DATE_PARSE_FORMAT),
					dayjs(dates.to)?.format(DATE_PARSE_FORMAT) ?? dayjs().format(DATE_PARSE_FORMAT),
				],
			},
			reportActionType:    SpotifyCreateReportActionType.Email,
			reportActionFormat:  SpotifyCreateReportActionFormat.Csv,
			reportActionOptions: reportId ? undefined : {
				actionOptionType:  SpotifyCreateReportActionOptionType.ActionEmails,
				actionOptionValue: [userMetaData?.userEmail ?? ''],
			},
			reportSchedule: getReportScheduleFromSchedule(schedule ?? SpotifyReportSchedule.OnlyOnce, time),
			// first value is always the main user group, owner
			userGroupIds:   !reportId ? userGroupIds : [...new Set([filters.userGroupId[0].value[0], ...privateUserGroupIds])],
		};

		if (lifetimeStats) {
			// @ts-ignore
			if (variables?.reportDateType) {
				// @ts-ignore
				delete variables.reportDateType;
			}
			// @ts-ignore
			delete variables.reportDateRangeOptions;
		}

		await (reportId ? onUpdate : onCreate)(
			// @ts-ignore
			{ variables },
		);
	};

	return { onCreateReport, loading: loading || loadingUpdate, error: creationError || loadingError };
};

export default useCreateReportMutation;
